<template>
  <PrintLocModal
    v-if="modalVisible"
    :plantList="plants"
    :selectedProductIds="modalProductIds"
    :open="modalVisible"
    :companySummary="companySummary"
    :label-type="labelType"
    :total-products="totalProductsNoFilters"
    @closeModal="onPrintModalClosed"
  />
  <div class="certificates-table" data-cy="certificates-table">
    <div
      class="certificates-table-header grid-control__container table-modifiers clearfi"
    >
      <Button
        theme-color="primary"
        @click.prevent="printLocSelectedProductsClicked"
        :disabled="selectedProductIds.length === 0"
        data-cy="print-selected-products-button"
      >
        Print LOC of {{ selectedProductIds.length }} selected items
      </Button>
      <Button
        theme-color="primary"
        backgroundColor="#fff"
        @click.prevent="printLocAllClicked"
        data-cy="print-all-products-button"
        :disabled="totalProductsNoFilters === 0"
      >
        Print LOC - All
      </Button>
      <div class="header-right-block">
        <TableSearch
          class="certificate-search"
          :value="searchInput"
          placeholder="Search by Label Name, Label Number, Brand, UKD ID"
          @searchSubmitted="onSearchSubmitted"
          data-cy="certificate-search"
        />
        <GridActionsMenu @actionClicked="onActionClicked" data-cy="grid-actions" />
      </div>
    </div>
    <CertificatesGrid
      :products="products"
      :productsLoading="productsLoading"
      :productsSkip="productsSkip"
      :pageSize="productsTake"
      :totalProducts="totalProducts"
      :filter="certificatesFilter"
      :sort="sortDescriptors"
      :getPropValues="getProductPropValuesAsync"
      ref="gridRef"
      @productsChanged="onProductsChanged"
      @selectionChanged="onSelectionChanged"
      @selectAllClicked="onSelectAllClickedAsync"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler
      :filter="certificatesFilter"
      ref="menuIconHandlerRef"
    />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch } from "vue";
import CertificatesGrid from "./KosherCertificatesGrid.vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import type {
  GetProductDisplayByCriteriaWithFilteringRequest,
  ProductWithSelection,
} from "@/api/kosherCertificates";
import type { IdAndName } from "@/Domain/IdAndName";
import PrintLocModal from "./PrintLocModal.vue";
import type { CompanySummary } from "@/Domain/CompanySummary";
import { EnumPrintLocType } from "@/Domain/EnumPrintLocType";
import { Button } from "@progress/kendo-vue-buttons";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

import {
  getJsonFilter,
  getFilterOperator,
  valueFilterConvert,
  getValueFilterDistinct,
  isFilterEmpty,
  FilterValuesTake,
  boolYNConvert,
  boolYNConvertBackToString,
} from "@/shared/helpers/filtersHelper";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { EnumConfidential } from "@/Domain/EnumConfidential";
import { FilterOperatorType } from "@/Domain/FilterOperatorType";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import {
  convertBackToNumber,
  convertNumber,
} from "@/Domain/EnumGroupConverter";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();
const props = defineProps<{
  plants: IdAndName[];
  daysBack: number;
  companySummary: CompanySummary;
  labelType: EnumPrintLocType;
}>();

const tableName = "KosherCertificatesTable";

const defaultTake = 25;

const searchInput = ref<string>("");
const products = ref<ProductWithSelection[]>([]);
const totalProducts = ref<number>(0);
const totalProductsNoFilters = ref<number>(0);
const productsLoading = ref<boolean>(false);
const productsSkip = ref<number>(0);
const certificatesFilter = ref<CompositeFilterDescriptor>(
  GridOptionsService.loadGridFilter(tableName)
);
const selectedProductIds = ref<string[]>([]);
const modalProductIds = ref<string[]>([]);
const modalVisible = ref<boolean>(false);
const productsTake = ref<number>(defaultTake);
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const allProductIds = ref<string[]>([]);
const allProductIdsLoading = ref<boolean>(false);

const gridRef = ref<InstanceType<typeof CertificatesGrid>>();
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const productsIdsRequest = computed(() => {
  return {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value || "",
    labelType:
      Object.keys(EnumPrintLocType)[
        Object.values(EnumPrintLocType).indexOf(props.labelType)
      ],
    plantList: props.plants.map((p) => p.ID).join(";"),
    daysBack: props.daysBack,
    groupByLabel: true,
    filters: buildJsonFilterString(
      certificatesFilter.value,
      valueFiltersConvertOptions
    ),
    skip: 0,
    take: 999999,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: "VueFullUkdId",
  };
});

const valueFiltersConvertOptions = [
  {
    column: "_Pesach",
    convert: boolYNConvert,
    convertBack: boolYNConvertBackToString,
  },
  {
    column: "_Confidential",
    convert: (value: string) => {
      return value != EnumConfidential.NotConfidential.toString() ? "Y" : "N";
    },
    convertBack: (value: string) => {
      return value;
    },
  },
  {
    column: "_Group",
    convert: (value: number) => {
      return convertNumber(value);
    },
    convertBack: (value: string) => {
      return convertBackToNumber(value);
    },
  },
] as ValueFieldConvertOption[];

const filtersApplied = computed(() => {
  return (
    searchInput.value ||
    !isFilterEmpty(certificatesFilter.value) ||
    sortField.value
  );
});

const buildJsonFilterString = function (
  filter: CompositeFilterDescriptor | null,
  valueFiltersConvertOptions: ValueFieldConvertOption[]
) {
  const result = getJsonFilter(filter, valueFiltersConvertOptions);

  // Custom logic for confidential filter.
  // If confidential filter is selected (Y) then we need to use all confidential values with in operator
  // (Confidential, UkdOnlyButNotPublicUkd, SomeOfTheCompanysProductsAreConfidentialAndSomeAreNot)
  // If not confidential filter is selected (N) then we use NotConfidential value with equal operator
  const confidentialGroup = result.FilterGroups.find(
    (fg) => fg.FieldName === "_Confidential"
  );
  if (!confidentialGroup) return JSON.stringify(result);

  const selectConfidential = confidentialGroup.FilterFields.find(
    (ff) => ff.Value.toLowerCase() === "y"
  );
  const selectNotConfidential = confidentialGroup.FilterFields.find(
    (ff) => ff.Value.toLowerCase() === "n"
  );

  if (selectConfidential) {
    confidentialGroup.FilterFields = [
      {
        Operator: "in",
        Value: EnumConfidential.Confidential.toString(),
      },
      {
        Operator: "in",
        Value: EnumConfidential.UkdOnlyButNotPublicUkd.toString(),
      },
      {
        Operator: "in",
        Value:
          EnumConfidential.SomeOfTheCompanysProductsAreConfidentialAndSomeAreNot.toString(),
      },
    ];
  } else if (selectNotConfidential) {
    const filterOperator = getFilterOperator(
      FilterOperatorType.Equal.toString()
    );
    confidentialGroup.FilterFields = [
      {
        Operator: filterOperator?.operator ?? "",
        Value: EnumConfidential.NotConfidential.toString(),
      },
    ];
  }

  return JSON.stringify(result);
};

const getProductDisplayAsync = async function (
  skip: number,
  take: number,
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value || "",
    labelType:
      Object.keys(EnumPrintLocType)[
        Object.values(EnumPrintLocType).indexOf(props.labelType)
      ],
    plantList: props.plants.map((p) => p.ID).join(";"),
    daysBack: props.daysBack,
    groupByLabel: true,
    filters: buildJsonFilterString(filter, valueFiltersConvertOptions),
    skip: skip,
    take: take,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetProductDisplayByCriteriaWithFilteringRequest;

  const endpoint = "GetProductDisplayByCriteriaWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  certificatesFilter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  // 13049: filter on the page should not remain active
  // GridOptionsService.saveGridFilter(tableName, certificatesFilter.value);

  sortField.value = "";
  sortAscending.value = false;
  searchMatchingProductsAsync();
  getAllProductsIdsAsync();
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  productsSkip.value = 0;
  searchMatchingProductsAsync(certificatesFilter.value);
  getAllProductsIdsAsync();
};

const onProductsChanged = function (options: ItemChangedOptions) {
  // Kendo Grid sending two duplicate events on filter change
  // If filterEvent.filter is null, it's the second event and we should ignore it.
  // FilterEvent is null when user change page size or page number
  // And in that case only one event is sent
  if (options.filterEvent) {
    if (!options.filterEvent.filter) return;
    certificatesFilter.value.filters = options.filterEvent.filter.filters;
    // 13049: filter on the page should not remain active
    // GridOptionsService.saveGridFilter(tableName, certificatesFilter.value);
  }
  if (productsTake.value != options.take) {
    productsTake.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  productsSkip.value = options.skip;
  searchMatchingProductsAsync(certificatesFilter.value);
  getAllProductsIdsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  productsSkip.value = 0;
  searchMatchingProductsAsync(certificatesFilter.value);
};

const onSelectionChanged = function (products: ProductWithSelection[]) {
  products.forEach((product) => {
    const index = selectedProductIds.value.findIndex(
      (p) => p === product.AgencyId
    );
    if (product.selected) {
      if (index === -1) selectedProductIds.value.push(product.AgencyId);
      else
        console.debug(
          `KosherCertificatesTable.vue: onSelectionChanged(): product already selected`,
          product,
          selectedProductIds.value[index]
        );
    } else if (index > -1) {
      selectedProductIds.value.splice(index, 1);
    }
  });
};

const getAllProductsIdsAsync = async function (ignoreFilters: boolean = false) {
  if (allProductIdsLoading.value) return;

  allProductIds.value = [];
  allProductIdsLoading.value = true;

  const params = productsIdsRequest.value;
  if (ignoreFilters) {
    params.filters = "";
    params.searchString = "";
  }

  const productPromise = CacheService.getResponseAsync(
    createCacheKey(params, "ProductDisplayIds"),
    () =>
      api.get(
        "GetProductDisplayByCriteriaWithFilteringValuesForFieldOnly",
        params
      )
  );

  await handleResponsePromiseAsync(
    productPromise,
    (data) => {
      allProductIds.value = data.ListItems;
      if (
        ignoreFilters ||
        (isFilterEmpty(certificatesFilter.value) && searchInput.value === "")
      )
        totalProductsNoFilters.value = data.TotalItemsInList;
    },
    "KosherCertificatesTable",
    "getAllProductsIdsAsync"
  );

  allProductIdsLoading.value = false;
};

const onSelectAllClickedAsync = async function (checked: boolean) {
  if (checked) {
    selectedProductIds.value = allProductIds.value;
  } else {
    selectedProductIds.value = [];
  }
};

const printLocSelectedProductsClicked = function () {
  modalProductIds.value = selectedProductIds.value;
  modalVisible.value = true;
};

const printLocAllClicked = function () {
  modalProductIds.value = [];
  modalVisible.value = true;
};

const onPrintModalClosed = function () {
  // 12875: Grid Row Selection(s) Should Be Removed After Printing LOC
  selectedProductIds.value = [];
  products.value.forEach((p) => {
    p.selected = false;
  });
  modalVisible.value = false;
  gridRef.value?.hideSelectionPopup();
};

const searchMatchingProductsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (productsLoading.value) return;
  try {
    productsLoading.value = true;

    // Show no products if no plants selected
    if (props.plants.length === 0) {
      products.value = [];
      totalProducts.value = 0;
      return;
    }

    let result = await getProductDisplayAsync(
      productsSkip.value,
      productsTake.value,
      filter
    );
    if (result.success) {
      products.value = result.response.ListItems;
      products.value.forEach((p) => {
        p.selected = selectedProductIds.value.some((sp) => sp === p.AgencyId);
      });
      totalProducts.value = result.response.TotalItemsInList;
    } else {
      console.error(
        "KosherCertificatesTable.vue: searchMatchingProductsAsync(): resp = " +
          result
      );
    }
    console.debug(
      `KosherCertificatesTable.vue: searchMatchingProductsAsync(), result:`,
      result
    );
    console.debug(
      `KosherCertificatesTable.vue: searchMatchingProductsAsync(), certificates:`,
      products
    );
  } catch (err) {
    console.error(
      "KosherCertificatesTable.vue: searchMatchingProductsAsync(): catch error = " +
        err
    );
  } finally {
    productsLoading.value = false;
  }
};

const getProductPropValuesAsync = async function (
  column: GridColumnPropsExtended
) {
  let result: ProductWithSelection[] = [];
  if (!column.field) return result;

  await handleResponsePromiseAsync(
    getProductDisplayAsync(0, 999999, certificatesFilter.value, column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
      result = getValueFilterDistinct(column.field!, result);
    },
    "KosherCertificatesTable.vue",
    "getProductPropValuesAsync"
  );
  return result;
};

const resetPagination = function () {
  if (productsTake.value === defaultTake) return;

  productsSkip.value = 0;
  productsTake.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  searchMatchingProductsAsync(certificatesFilter.value);
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const onActionClicked = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      break;
    case GridMenuActionType.ResetColumnOrder:
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetPagination();
      updateColumnMenuIcons();
      break;
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

watch(
  () => [props.plants, props.daysBack],
  async () => {
    productsSkip.value = 0;
    selectedProductIds.value = [];
    searchMatchingProductsAsync(certificatesFilter.value);
    getAllProductsIdsAsync();
  }
);

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) productsTake.value = pageSize;
  searchMatchingProductsAsync(certificatesFilter.value);
  await getAllProductsIdsAsync();
  // Send additional request to get all product ids if there are filters applied
  // Both filter and search input have empty values by default
  // this check is added just in case if we want to change this behavior in the future
  if (!isFilterEmpty(certificatesFilter.value) || searchInput.value !== "")
    getAllProductsIdsAsync(true);
});
</script>

<style lang="scss">
.certificates-table {
  margin-bottom: 24px;

  .certificates-table-header {
    display: flex;
    gap: 12px;

    .header-right-block {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .certificate-search {
        width: 400px;
        margin-right: 16px;
      }
    }
  }

  .btn {
    border-radius: 2px;
    border-bottom-width: 2px;
    font-weight: 200;
    font-size: 12px;
    cursor: pointer;

    &.btn-primary {
      background-color: #1a73e8;
      border-color: #1a73e8;
      color: #fff;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      padding: 8px 16px;
      border-radius: 4px;
      margin-right: 16px;
    }

    &:disabled {
      opacity: 0.65;
    }
  }
}
</style>
